import emailjs from '@emailjs/browser';

// 邮件发送接口
export interface ConsultationData {
  productName: string;
  name: string;
  phone: string;
  email: string;
  business: string;
  fax?: string;
}

// EmailJS 配置
const EMAILJS_CONFIG = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  enabled: process.env.REACT_APP_EMAILJS_ENABLED === 'true' || true // 临时强制启用
};

// 初始化 EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

// 发送咨询邮件 - 使用 EmailJS
export const sendConsultationEmail = async (data: ConsultationData): Promise<{ success: boolean; message: string; messageId?: string }> => {
  try {
    // 检查配置
    if (!EMAILJS_CONFIG.enabled) {
      throw new Error('EmailJS 未启用，请检查环境变量 REACT_APP_EMAILJS_ENABLED');
    }

    if (!EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templateId || !EMAILJS_CONFIG.publicKey) {
      const missing = [];
      if (!EMAILJS_CONFIG.serviceId) missing.push('Service ID');
      if (!EMAILJS_CONFIG.templateId) missing.push('Template ID');
      if (!EMAILJS_CONFIG.publicKey) missing.push('Public Key');
      throw new Error(`EmailJS 配置不完整，缺少: ${missing.join(', ')}`);
    }

    const { productName, name, phone, email, business, fax } = data;

    // 准备邮件参数
    const currentTime = new Date().toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Shanghai'
    });

    const templateParams = {
      // 产品信息
      product_name: productName,
      company_name: '柯熙美诺生物科技有限公司',
      
      // 客户信息
      from_name: name,
      from_phone: phone,
      from_email: email,
      business_type: business,
      fax_number: fax || '未提供',
      
      // 回复信息
      reply_to: email,
      to_email: process.env.REACT_APP_COMPANY_EMAIL || 'your-company@example.com',
      current_time: currentTime,
      
      // 额外信息
      user_agent: navigator.userAgent,
      referrer: document.referrer || '直接访问',
      timestamp: Date.now()
    };

    console.log('发送邮件参数:', templateParams);

    // 发送邮件
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams
    );

    console.log('EmailJS 邮件发送成功:', response);
    return {
      success: true,
      message: '邮件发送成功',
      messageId: response.text
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '未知错误';
    console.error('EmailJS 邮件发送失败:', error);
    
    return {
      success: false,
      message: `邮件发送失败: ${errorMessage}`
    };
  }
};

// 检查 EmailJS 配置是否完整
export const checkEmailJSConfig = (): { valid: boolean; missing: string[] } => {
  const missing: string[] = [];
  
  if (!EMAILJS_CONFIG.serviceId) missing.push('Service ID');
  if (!EMAILJS_CONFIG.templateId) missing.push('Template ID');
  if (!EMAILJS_CONFIG.publicKey) missing.push('Public Key');
  
  return {
    valid: missing.length === 0,
    missing
  };
};

// 调试工具函数
export const debugEmailConfig = () => {
  console.log('=== EmailJS 配置调试信息 ===');
  console.log('EmailJS 配置:', {
    enabled: EMAILJS_CONFIG.enabled,
    serviceId: EMAILJS_CONFIG.serviceId ? '已配置' : '未配置',
    templateId: EMAILJS_CONFIG.templateId ? '已配置' : '未配置',
    publicKey: EMAILJS_CONFIG.publicKey ? '已配置' : '未配置'
  });
  
  const configCheck = checkEmailJSConfig();
  console.log('配置检查:', configCheck);
  
  console.log('环境变量:', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_EMAILJS_ENABLED: process.env.REACT_APP_EMAILJS_ENABLED,
    REACT_APP_EMAILJS_SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID ? '已设置' : '未设置',
    REACT_APP_EMAILJS_TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID ? '已设置' : '未设置',
    REACT_APP_EMAILJS_PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? '已设置' : '未设置'
  });
  
  return {
    emailjs: EMAILJS_CONFIG,
    configCheck,
    env: {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_EMAILJS_ENABLED: process.env.REACT_APP_EMAILJS_ENABLED
    }
  };
};

export default sendConsultationEmail;